import React from "react";
import { Alert, Snackbar } from "@mui/material";
import NotificationIcon from "@mui/icons-material/Notifications";
import CelebrationIcon from "@mui/icons-material/Celebration";
import { JobMonitorDrawer } from "./components";
import { useJobMonitorState } from "./hooks";

// Shape of the job monitor UI state so it can be shared across components
export type JobMonitorState = ReturnType<typeof useJobMonitorState>;

interface JobMonitorProps {
  state: JobMonitorState;
}

// Pure presentational component that renders the monitor using provided state
export const JobMonitorView: React.FC<JobMonitorProps> = ({ state }) => {
  const {
    open,
    toggleDrawer,
    showToast,
    toastMessage,
    handleCloseToast,
    showCompletionToast,
    setShowCompletionToast,
    lastCompletedJob,
    retryingJob,
    activeFilter,
    setActiveFilter,
    jobCounts,
    filteredJobs,
    newJobIds,
    completedJobIds,
    handleRemoveJob,
    handleRetryJob,
    handleJobClick,
    isMobile,
    activeJobsCount,
  } = state;

  return (
    <>
      <Snackbar
        open={showToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="info"
          sx={{ alignItems: "center" }}
          icon={<NotificationIcon />}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showCompletionToast}
        autoHideDuration={4000}
        onClose={() => setShowCompletionToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={(event) => {
            // The close button sits inside the Alert root; without this the
            // click bubbles to the root onClick and navigates to the report.
            event.stopPropagation();
            setShowCompletionToast(false);
          }}
          onClick={() => {
            if (lastCompletedJob?.reportId) {
              setShowCompletionToast(false);
              handleJobClick(lastCompletedJob);
            }
          }}
          severity="success"
          sx={{
            alignItems: "center",
            cursor: lastCompletedJob?.reportId ? "pointer" : "default",
          }}
          icon={<CelebrationIcon />}
        >
          {lastCompletedJob?.reportId
            ? "Report completed! Click to view."
            : "Report completed!"}
        </Alert>
      </Snackbar>

      <JobMonitorDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        jobCounts={jobCounts}
        filteredJobs={filteredJobs}
        handleRemoveJob={handleRemoveJob}
        handleRetryJob={handleRetryJob}
        handleJobClick={handleJobClick}
        newJobIds={newJobIds}
        completedJobIds={completedJobIds}
        retryingJob={retryingJob}
        isMobile={isMobile}
        activeJobsCount={activeJobsCount}
      />
    </>
  );
};

// NOTE: JobMonitorView must be mounted exactly once, at the layout level
// (MainComponent). Per-page mounts caused duplicate toasts/drawers and tied
// the job-status polling lifecycle to page navigation, freezing progress on
// pages without a monitor.
